/**
 * Gameplan persistence + utilities.
 *
 * v1 backing store is localStorage under a single versioned key.
 * Schema migrations land here when the shape changes — bump the
 * suffix on STORAGE_KEY and add a migration step in `loadGameplans`
 * that reads the old key, transforms, writes the new one, and
 * removes the old.
 *
 * All helpers swallow storage errors (Safari private mode, quota
 * exceeded, SSR contexts where `localStorage` is undefined) and
 * return safe defaults so the rest of the app never sees a thrown
 * error from a storage hiccup.
 */
import type { Gameplan, Line, LineEdge, LineNode, LineNodeRef } from '../types/gameplan';

const STORAGE_KEY = 'bjjmap:gameplans:v1';

/**
 * Tiny id factory used for every gameplan / line / node / edge.
 * Prefix lets us tell them apart at a glance in dev tools and avoid
 * collisions across kinds. Random suffix is base36-noise (~24 bits)
 * — comfortably below collision risk for any single user's local
 * data volume.
 */
export function makeId(prefix: 'gp' | 'ln' | 'n' | 'e'): string {
  const t = Date.now().toString(36);
  const r = Math.random().toString(36).slice(2, 8);
  return `${prefix}_${t}_${r}`;
}

/**
 * Read the persisted gameplans array from localStorage. Returns []
 * on a fresh install, a parse failure, or any storage error.
 */
/**
 * Build a simple linear line: a chain of refs connected head-to-tail.
 * Ids are deterministic (derived from the line id) so seeded plans keep
 * stable ids across reloads until the user first mutates them.
 */
function seedLine(id: string, name: string, refs: LineNodeRef[]): Line {
  const nodes: LineNode[] = refs.map((ref, i) => ({ id: `${id}-n${i}`, ref }));
  const edges: LineEdge[] = [];
  for (let i = 0; i < nodes.length - 1; i++) {
    edges.push({ id: `${id}-e${i}`, from: nodes[i].id, to: nodes[i + 1].id });
  }
  return { id, name, nodes, edges };
}

/**
 * Example gameplans shown to first-time users (no saved data yet) so the
 * feature is self-explanatory. All refs use real dataset ids. Returned
 * only when localStorage is empty — once the user mutates anything the
 * persisted array takes over, and deleting them all (→ stored `[]`) is
 * respected (we never re-seed over a non-null blob).
 */
function seedGameplans(): Gameplan[] {
  const now = Date.now();
  const pos = (id: string): LineNodeRef => ({ kind: 'position', id });
  const mv = (id: string): LineNodeRef => ({ kind: 'move', id });
  return [
    {
      id: 'gp_seed_closed_guard',
      name: 'Closed Guard Basics',
      description:
        'Example plan — bottom closed-guard sweeps & submissions. Edit or delete freely.',
      createdAt: now,
      updatedAt: now,
      lines: [
        seedLine('ln_seed_cg_sweep', 'Sweep to Mount', [
          pos('standing'),
          mv('guardpull'),
          pos('closedguard-bottom'),
          mv('scissorsweep'),
          pos('mount-top'),
          mv('armbar'),
          pos('submitted'),
        ]),
        seedLine('ln_seed_cg_sub', 'Submit from Guard', [
          pos('closedguard-bottom'),
          mv('trianglechoke'),
          pos('submitted'),
        ]),
      ],
    },
    {
      id: 'gp_seed_takedown_back',
      name: 'Takedown to the Back',
      description:
        'Example plan — top pressure from a double leg through to the back. Edit or delete freely.',
      createdAt: now,
      updatedAt: now,
      lines: [
        seedLine('ln_seed_tb_main', 'Double Leg → RNC', [
          pos('standing'),
          mv('doublelegtakedown'),
          pos('sidecontrol-top'),
          mv('kneedrive'),
          pos('mount-top'),
          mv('inserthook'),
          pos('backcontrol-top'),
          mv('rearnakedchoke'),
          pos('submitted'),
        ]),
      ],
    },
  ];
}

export function loadGameplans(): Gameplan[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return seedGameplans();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    // Light shape validation. We don't fully type-check — we trust
    // we wrote the data — but defend against a corrupted blob.
    return parsed.filter(
      (g): g is Gameplan =>
        !!g &&
        typeof g === 'object' &&
        typeof (g as Gameplan).id === 'string' &&
        typeof (g as Gameplan).name === 'string' &&
        Array.isArray((g as Gameplan).lines),
    );
  } catch {
    return [];
  }
}

/**
 * Write the gameplans array back to localStorage. Best-effort —
 * silently no-ops on storage errors (full quota, private mode).
 * Callers should not assume success.
 */
export function saveGameplans(plans: Gameplan[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(plans));
  } catch {
    // ignore — losing one save is acceptable; complaining isn't.
  }
}

// ------------ Factories ------------
// Pure constructors. They never mutate inputs and never touch
// localStorage themselves — the store calls them, threads the
// updated structures back into its state, and lets the
// store-level persistence subscription handle the write.

export function newGameplan(name: string, description?: string): Gameplan {
  const now = Date.now();
  return {
    id: makeId('gp'),
    name,
    description,
    createdAt: now,
    updatedAt: now,
    lines: [],
  };
}

export function newLine(name: string): Line {
  return {
    id: makeId('ln'),
    name,
    nodes: [],
    edges: [],
  };
}

export function newLineNode(ref: LineNodeRef, notes?: string): LineNode {
  return {
    id: makeId('n'),
    ref,
    notes,
  };
}

export function newLineEdge(
  from: string,
  to: string,
  label?: string,
): LineEdge {
  return {
    id: makeId('e'),
    from,
    to,
    label,
  };
}

// ------------ Immutable update helpers ------------
// These take a Gameplan[] and return a new Gameplan[] with the
// requested change applied, keeping the rest unchanged. Used by
// the store's action reducers — keeps mutation logic out of
// store.ts.

export function withGameplanUpdate(
  plans: Gameplan[],
  gameplanId: string,
  update: (g: Gameplan) => Gameplan,
): Gameplan[] {
  return plans.map((g) =>
    g.id === gameplanId ? { ...update(g), updatedAt: Date.now() } : g,
  );
}

export function withLineUpdate(
  plans: Gameplan[],
  gameplanId: string,
  lineId: string,
  update: (l: Line) => Line,
): Gameplan[] {
  return withGameplanUpdate(plans, gameplanId, (g) => ({
    ...g,
    lines: g.lines.map((l) => (l.id === lineId ? update(l) : l)),
  }));
}
